import {useEffect,useState} from 'react';
import {Button} from '../ui/button';
import {Input} from '../ui/input';
import PaymentBrand from './PaymentBrand';

const usd = new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'});
const field = 'rounded-lg border border-white/20 bg-[#111824] px-3 py-2 text-base text-white';
function imageData(file) {
  return new Promise((ok,fail)=>{
    const reader=new FileReader();
    reader.onload=()=>ok(String(reader.result).split(',')[1]);
    reader.onerror=()=>fail(new Error('Cannot read this receipt.'));
    reader.readAsDataURL(file);
  });
}

export default function ManualCashbook({client,tenant,finance=false}) {
  const base=finance?'/api/finance/manual-cashbook/'+tenant:'/api/admin/manual-cashbook';
  const [status,setStatus]=useState(finance?'pending':'all'),[page,setPage]=useState(1);
  const [rows,setRows]=useState(null),[more,setMore]=useState(false),[version,setVersion]=useState(0);
  const [busy,setBusy]=useState(false),[error,setError]=useState(''),[opened,setOpened]=useState(null);
  useEffect(()=>{
    let active=true;setRows(null);setError('');setOpened(null);
    client.get(base,{params:{page,status}}).then(({data})=>{
      if(active){setRows(data.rows);setMore(data.more);}
    }).catch(()=>{if(active)setError('Cannot load entries. Refresh to try again.');});
    return ()=>{active=false;};
  },[base,page,status,version,client]);
  const reload=()=>setVersion(v=>v+1);
  async function action(fn){
    if(busy)return;setBusy(true);setError('');
    try{await fn();}catch(e){const msg=e.response?.data?.detail;setError(typeof msg==='string'?msg:e.message||'Could not save. Refresh and check the entry.');}
    finally{setBusy(false);}
  }
  async function submit(e){
    e.preventDefault();const form=e.currentTarget,f=new FormData(form);
    await action(async()=>{
      const file=f.get('receipt');
      if(!file?.size||file.size>1000000)throw new Error('Select a receipt image under 1 MB.');
      await client.post(base,{direction:f.get('direction'),amount_cents:Math.round(Number(f.get('amount'))*100),
        method:f.get('method'),reference:f.get('reference'),proof:await imageData(file)});
      form.reset();setStatus('all');setPage(1);reload();
    });
  }
  return <section className="space-y-4 rounded-xl border border-white/10 bg-[#131822] p-4 text-white">
    <header><h2 className="text-xl font-semibold">Manual pay-ins &amp; payouts</h2>
      <p className="text-sm text-gray-300">CF approval counts the entry in its original month. No player wallet is involved.</p></header>
    {!finance&&<form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
      <label>Direction<select className={field+' block w-full'} name="direction"><option value="in">Manual In</option><option value="out">Manual Out</option></select></label>
      <label>Amount (USD)<Input required type="number" min="0.01" max="1000000" step="0.01" name="amount"/></label>
      <label>Payment method<Input required maxLength="80" name="method" placeholder="e.g. Chime"/></label>
      <label>Transaction reference<Input required maxLength="120" name="reference"/></label>
      <label>Receipt photo (up to 1 MB)<Input required type="file" name="receipt" accept="image/jpeg,image/png,image/webp"/></label>
      <Button disabled={busy} className="min-h-11 self-end bg-[#D4AF37] text-black hover:bg-[#F5D76E]">Submit for CF review</Button>
    </form>}
    <div className="flex items-center gap-3"><label>Status <select disabled={busy} className={field} value={status} onChange={e=>{setStatus(e.target.value);setPage(1);}}>
      {['all','pending','approved','declined'].map(s=><option key={s} value={s}>{s}</option>)}</select></label>
      <Button disabled={busy} variant="outline" onClick={reload}>Refresh</Button></div>
    {error&&<p role="alert" className="text-red-300">{error}</p>}
    {!rows&&!error&&<p role="status">Loading entries…</p>}
    {rows?.length===0&&<p>No entries with this status.</p>}
    {rows?.map(r=><article key={r._id} className="space-y-2 border-t border-white/10 py-3">
      <div className="flex flex-wrap justify-between gap-2"><strong>{r.direction==='in'?'Manual In':'Manual Out'} · {usd.format(r.amount_cents/100)}</strong><span>{r.status}</span></div>
      <p className="break-words"><PaymentBrand method={r.method}/> · {r.reference}</p>
      <p className="text-sm text-gray-300">Entered by {r.submitted_name} · {new Date(r.submitted_at).toLocaleString()}</p>
      <p className="text-sm text-gray-300">Reporting month: {r.entry_month} ({r.reporting_timezone})</p>
      {r.reviewed_at&&<p className="text-sm text-gray-300">Reviewed by {r.reviewed_name} · {new Date(r.reviewed_at).toLocaleString()}</p>}
      {finance&&<Button variant="outline" disabled={busy} onClick={()=>action(async()=>{
        setOpened(null);const {data}=await client.get(base+'/'+r._id+'/proof');setOpened({id:r._id,image:data.jpeg,ready:false});
      })}>{opened?.id===r._id&&opened.failed?'Retry receipt':'Review receipt'}</Button>}
      {opened?.id===r._id&&<div className="space-y-2">
        {!opened.failed&&<img className="max-h-96 max-w-full object-contain" src={'data:image/jpeg;base64,'+opened.image} alt="Submitted transaction receipt"
          onLoad={()=>setOpened(v=>v?.id===r._id?{...v,ready:true}:v)}
          onError={()=>setOpened(v=>v?.id===r._id?{...v,ready:false,failed:true}:v)}/>}
        {opened.failed&&<p role="alert">Receipt could not be displayed. Retry before making a decision.</p>}
        {r.status==='pending'&&opened.ready&&!opened.decision&&['approved','declined'].map(s=><Button key={s} variant="outline"
          className={'mr-2 min-h-11 '+(s==='approved'?'text-emerald-300':'text-red-300')} disabled={busy} onClick={()=>setOpened(v=>({...v,decision:s}))}>
          {s==='approved'?'Approve':'Decline'} {usd.format(r.amount_cents/100)}</Button>)}
        {opened.decision&&<div><p>{opened.decision==='approved'?'Include this entry in':'Exclude this entry from'} {r.entry_month} totals?</p>
          <Button className="mr-2 min-h-11 bg-[#D4AF37] text-black" disabled={busy} onClick={()=>action(async()=>{
            await client.post(base+'/'+r._id,{status:opened.decision});reload();
          })}>{opened.decision==='approved'?'Confirm approval':'Confirm decline'}</Button>
          <Button variant="outline" disabled={busy} onClick={()=>setOpened(v=>({...v,decision:null}))}>Cancel</Button></div>}
        <Button variant="outline" onClick={()=>setOpened(null)}>Close receipt</Button></div>}
    </article>)}
    {rows&&(page>1||more)&&<div><Button disabled={busy||page===1} onClick={()=>setPage(page-1)}>Previous</Button> Page {page} <Button disabled={busy||!more} onClick={()=>setPage(page+1)}>Next</Button></div>}
  </section>;
}
